import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { getCompletionsForReport, getSessionsForAdmin, deleteCompletion } from '../utils/quizSessionManager';
import { ChevronDown, ChevronRight, FileBarChart, Search, ExternalLink, Trash2, FileDown, Download } from 'lucide-react';
import { toastManager } from '../utils/toastManager';
import { generateAttemptPdf, generateConsultantReportPdf, generateAllConsultantsReportPdf } from '../utils/pdfReport';
import './Reports.css';

const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return { toDate: () => d, toMillis: () => d.getTime() };
};

/** Sample reports when Firestore is empty / preview without data */
const SAMPLE_SESSIONS = [
  { id: 'demo-session-icd10', title: 'ICD-10 Coding Basics', quizFileName: 'ICD-10 Coding Basics' },
  { id: 'demo-session-compliance', title: 'Compliance Essentials', quizFileName: 'Compliance Essentials' },
];

const SAMPLE_COMPLETIONS = [
  {
    id: 'demo-c1',
    sessionId: 'demo-session-icd10',
    sessionTitle: 'ICD-10 Coding Basics',
    userEmail: 'priya.consultant@diologic.com',
    playerName: 'Priya Sharma',
    score: 8,
    totalQuestions: 10,
    completedAt: daysAgo(2),
    answers: [
      { questionText: 'What does ICD stand for?', selectedAnswerText: 'International Classification of Diseases', correctAnswerText: 'International Classification of Diseases', isCorrect: true },
      { questionText: 'ICD-10 codes begin with which character type?', selectedAnswerText: 'Letter', correctAnswerText: 'Letter', isCorrect: true },
      { questionText: 'How many characters can an ICD-10 code have?', selectedAnswerText: '5', correctAnswerText: '3 to 7', isCorrect: false },
      { questionText: 'Which chapter covers diseases of the circulatory system?', selectedAnswerText: 'Chapter IX', correctAnswerText: 'Chapter IX', isCorrect: true },
    ],
  },
  {
    id: 'demo-c2',
    sessionId: 'demo-session-icd10',
    sessionTitle: 'ICD-10 Coding Basics',
    userEmail: 'arjun.consultant@diologic.com',
    playerName: 'Arjun Patel',
    score: 6,
    totalQuestions: 10,
    completedAt: daysAgo(3),
    answers: [
      { questionText: 'What does ICD stand for?', selectedAnswerText: 'Internal Clinical Document', correctAnswerText: 'International Classification of Diseases', isCorrect: false },
      { questionText: 'ICD-10 codes begin with which character type?', selectedAnswerText: 'Letter', correctAnswerText: 'Letter', isCorrect: true },
      { questionText: 'How many characters can an ICD-10 code have?', selectedAnswerText: '3 to 7', correctAnswerText: '3 to 7', isCorrect: true },
    ],
  },
  {
    id: 'demo-c3',
    sessionId: 'demo-session-compliance',
    sessionTitle: 'Compliance Essentials',
    userEmail: 'meera.consultant@diologic.com',
    playerName: 'Meera Rao',
    score: 9,
    totalQuestions: 10,
    completedAt: daysAgo(1),
    answers: [
      { questionText: 'HIPAA primarily protects what?', selectedAnswerText: 'Patient health information', correctAnswerText: 'Patient health information', isCorrect: true },
      { questionText: 'When should a privacy breach be reported?', selectedAnswerText: 'Within required timelines', correctAnswerText: 'Within required timelines', isCorrect: true },
      { questionText: 'PHI can be shared freely on social media.', selectedAnswerText: 'True', correctAnswerText: 'False', isCorrect: false },
    ],
  },
  {
    id: 'demo-c4',
    sessionId: 'demo-session-compliance',
    sessionTitle: 'Compliance Essentials',
    userEmail: 'priya.consultant@diologic.com',
    playerName: 'Priya Sharma',
    score: 7,
    totalQuestions: 10,
    completedAt: daysAgo(5),
    answers: [
      { questionText: 'HIPAA primarily protects what?', selectedAnswerText: 'Patient health information', correctAnswerText: 'Patient health information', isCorrect: true },
      { questionText: 'PHI can be shared freely on social media.', selectedAnswerText: 'False', correctAnswerText: 'False', isCorrect: true },
    ],
  },
];

const isDemoId = (id) => String(id || '').startsWith('demo-');

const Reports = ({ userEmail, isAdmin }) => {
  const [completions, setCompletions] = useState([]);
  const [adminSessions, setAdminSessions] = useState([]);
  const [usingSample, setUsingSample] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedSessions, setExpandedSessions] = useState(new Set());
  const [expandedUsers, setExpandedUsers] = useState(new Set());
  const [selectedReportIds, setSelectedReportIds] = useState(new Set());
  const [deleteCompletionConfirm, setDeleteCompletionConfirm] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const applySampleData = useCallback(() => {
    if (isAdmin) {
      setCompletions(SAMPLE_COMPLETIONS);
      setAdminSessions(SAMPLE_SESSIONS);
    } else {
      const mine = SAMPLE_COMPLETIONS.filter((c) => c.userEmail.includes('priya')).map((c) => ({
        ...c,
        id: `${c.id}-self`,
        userEmail: userEmail || c.userEmail,
        playerName: 'You (sample)',
      }));
      setCompletions(mine);
      setAdminSessions([]);
    }
    setUsingSample(true);
    setExpandedSessions(new Set([SAMPLE_SESSIONS[0].id]));
  }, [isAdmin, userEmail]);

  const loadData = useCallback(async () => {
    // Consultant reports need an email; admin can load all without sign-in (local preview)
    if (!isAdmin && !userEmail) {
      applySampleData();
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const email = userEmail || 'preview@local';
      const [completionsData, sessionsData] = await Promise.all([
        getCompletionsForReport(email, isAdmin),
        isAdmin ? getSessionsForAdmin(email) : Promise.resolve([]),
      ]);
      const empty =
        (!completionsData || completionsData.length === 0) &&
        (!sessionsData || sessionsData.length === 0);
      if (empty) {
        applySampleData();
      } else {
        setCompletions(completionsData);
        setAdminSessions(sessionsData);
        setUsingSample(false);
      }
    } catch (err) {
      console.error('Failed to load reports:', err);
      applySampleData();
    } finally {
      setLoading(false);
    }
  }, [userEmail, isAdmin, applySampleData]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshTrigger]);

  // Admin: track by session created by admin. Consultant: group completions by session.
  const sessionsGrouped = useMemo(() => {
    if (isAdmin && adminSessions.length > 0) {
      // Build from admin-created sessions: each session shows its completions
      return adminSessions.map((s) => {
        const sessionCompletions = completions
          .filter((c) => c.sessionId === s.id)
          .sort((a, b) => {
            const ta = a.completedAt?.toMillis?.() ?? 0;
            const tb = b.completedAt?.toMillis?.() ?? 0;
            return tb - ta;
          });
        return {
          sessionId: s.id,
          sessionTitle: s.title || s.quizFileName || 'Quiz',
          completions: sessionCompletions,
        };
      });
    }
    // Consultant: group completions by session (sessionId + sessionTitle)
    const bySession = {};
    completions.forEach((c) => {
      const key = c.sessionId || c.sessionTitle || 'unknown';
      const title = c.sessionTitle || 'Quiz';
      if (!bySession[key]) {
        bySession[key] = { sessionId: c.sessionId, sessionTitle: title, completions: [] };
      }
      bySession[key].completions.push(c);
    });
    const millis = (c) => c.completedAt?.toMillis?.() ?? 0;
    return Object.values(bySession)
      .map((s) => ({
        ...s,
        completions: s.completions.sort((a, b) => millis(a) - millis(b)),
      }))
      .sort((a, b) => millis(a.completions[0]) - millis(b.completions[0]));
  }, [completions, adminSessions, isAdmin]);

  // Filter by search (session title or user email)
  const filteredSessions = useMemo(() => {
    const q = (searchQuery || '').toLowerCase().trim();
    if (!q) return sessionsGrouped;
    return sessionsGrouped
      .map((s) => ({
        ...s,
        completions: s.completions.filter(
          (c) =>
            (s.sessionTitle || '').toLowerCase().includes(q) ||
            (c.userEmail || '').toLowerCase().includes(q)
        ),
      }))
      .filter((s) => s.completions.length > 0);
  }, [sessionsGrouped, searchQuery]);

  const toggleSession = (sessionId) => {
    setExpandedSessions((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  const toggleUser = (completionId) => {
    setExpandedUsers((prev) => {
      const next = new Set(prev);
      if (next.has(completionId)) next.delete(completionId);
      else next.add(completionId);
      return next;
    });
  };

  const toggleReportSelection = (completionId, e) => {
    e.stopPropagation();
    setSelectedReportIds((prev) => {
      const next = new Set(prev);
      if (next.has(completionId)) next.delete(completionId);
      else next.add(completionId);
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedReportIds(new Set());
  };

  const reportViewUrl = (completionId) =>
    `${window.location.origin}/report-view?id=${encodeURIComponent(completionId)}`;

  // Single quiz attempt report (per row).
  const downloadAttemptReport = async (e, completion) => {
    e.stopPropagation();
    try {
      await generateAttemptPdf({
        player: { displayName: completion.playerName, email: completion.userEmail },
        userEmail: completion.userEmail,
        sessionTitle: completion.sessionTitle,
        score: completion.score,
        totalQuestions: completion.totalQuestions,
        answers: completion.answers || [],
        completedAt: completion.completedAt?.toDate?.() || undefined,
      });
      toastManager.success('Report downloaded');
    } catch (err) {
      console.error('Failed to generate report:', err);
      toastManager.error('Failed to generate PDF');
    }
  };

  // Total: all of a consultant's quizzes combined into one PDF.
  const downloadAllQuizzesReport = async () => {
    if (!completions.length) {
      toastManager.error('No completions to report');
      return;
    }
    try {
      await generateConsultantReportPdf({
        userEmail,
        playerName: completions[0]?.playerName,
        completions,
      });
      toastManager.success('Total quizzes report downloaded');
    } catch (err) {
      console.error('Failed to generate total quizzes report:', err);
      toastManager.error('Failed to generate PDF');
    }
  };

  const downloadTotalReport = async () => {
    if (!completions.length) {
      toastManager.error('No completions to report');
      return;
    }
    try {
      await generateAllConsultantsReportPdf({ completions });
      toastManager.success('Total consultants report downloaded');
    } catch (err) {
      console.error('Failed to generate total report:', err);
      toastManager.error('Failed to generate PDF');
    }
  };

  const handleDeleteCompletion = async (completionId) => {
    if (!completionId) return;
    setDeleteCompletionConfirm(null);
    if (isDemoId(completionId) || usingSample) {
      toastManager.info('Sample reports cannot be deleted');
      return;
    }
    try {
      await deleteCompletion(completionId);
      toastManager.success('Report deleted');
      setSelectedReportIds((prev) => {
        const next = new Set(prev);
        next.delete(completionId);
        return next;
      });
      setRefreshTrigger((t) => t + 1);
    } catch (err) {
      console.error('Failed to delete report:', err);
      toastManager.error(err.message || 'Failed to delete report');
    }
  };

  const handleDeleteSelected = async () => {
    const ids = Array.from(selectedReportIds);
    if (ids.length === 0) return;
    if (usingSample || ids.every(isDemoId)) {
      toastManager.info('Sample reports cannot be deleted');
      return;
    }
    setDeleteCompletionConfirm({ bulk: true, ids });
  };

  const handleBulkDelete = async () => {
    const { ids } = deleteCompletionConfirm;
    setDeleteCompletionConfirm(null);
    const realIds = ids.filter((id) => !isDemoId(id));
    if (realIds.length === 0) {
      toastManager.info('Sample reports cannot be deleted');
      return;
    }
    try {
      await Promise.all(realIds.map((id) => deleteCompletion(id)));
      toastManager.success(`${realIds.length} report(s) deleted`);
      setSelectedReportIds(new Set());
      setRefreshTrigger((t) => t + 1);
    } catch (err) {
      console.error('Failed to delete reports:', err);
      toastManager.error(err.message || 'Failed to delete reports');
    }
  };

  if (loading) {
    return (
      <div className="reports-card">
        <div className="reports-loading">
          <div className="loading-spinner"></div>
          <p>Loading reports...</p>
        </div>
      </div>
    );
  }

  const hasSessions = sessionsGrouped.length > 0;
  if (!loading && !hasSessions) {
    return (
      <div className="reports-card">
        <div className="reports-empty">
          <FileBarChart size={48} className="reports-empty-icon" />
          <h3>No reports yet</h3>
          <p>{isAdmin ? 'Create quiz sessions and completions will appear here.' : 'Your quiz completions will appear here.'}</p>
        </div>
      </div>
    );
  }

  const hasSelection = selectedReportIds.size > 0;

  return (
    <div className="reports-card">
      {usingSample && (
        <div className="reports-sample-banner">
          Showing sample reports for preview — real completions will replace these when available.
        </div>
      )}
      <div className="reports-toolbar">
        <div className="reports-search">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Search by session or consultant email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        {hasSelection && (
          <div className="reports-selection-actions">
            <span className="reports-selected-count">{selectedReportIds.size} selected</span>
            <button type="button" className="btn-select-all" onClick={clearSelection}>Clear</button>
            <button type="button" className="btn-delete-selected" onClick={handleDeleteSelected}>
              <Trash2 size={16} />
              Delete selected
            </button>
          </div>
        )}
        {!isAdmin && (
          <button
            type="button"
            className="btn-download-all"
            onClick={downloadAllQuizzesReport}
            title="Download all quizzes in a single PDF report"
          >
            <FileDown size={16} />
            Download all reports
          </button>
        )}
        {isAdmin && (
          <button
            type="button"
            className="btn-download-all"
            onClick={downloadTotalReport}
            title="Download a PDF summary for all consultants"
          >
            <Download size={16} />
            Download all reports
          </button>
        )}
      </div>

      <div className="reports-list reports-by-session">
        {filteredSessions.map((session) => {
          const sessionKey = session.sessionId || session.sessionTitle || 'unknown';
          const isSessionExpanded = expandedSessions.has(sessionKey);
          const firstC = session.completions[0];
          const consultantName = firstC?.playerName?.trim() || firstC?.userEmail || 'Consultant';
          const quizDate = firstC?.completedAt?.toDate?.()
            ? firstC.completedAt.toDate().toLocaleDateString()
            : '—';
          const sessionMeta = session.completions.length > 1
            ? `${session.completions.length} consultants`
            : `${consultantName} · ${quizDate}`;
          return (
            <div key={sessionKey} className="report-session">
              <div
                className="report-session-header"
                onClick={() => toggleSession(sessionKey)}
              >
                <span className="report-expand-icon">
                  {isSessionExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </span>
                <div className="report-session-summary">
                  <span className="report-session-title">{session.sessionTitle || 'Quiz'}</span>
                  <span className="report-session-meta">{sessionMeta}</span>
                </div>
              </div>

              {isSessionExpanded && (
                <div className="report-session-users">
                  {session.completions.map((c) => {
                    const isUserExpanded = expandedUsers.has(c.id);
                    const answers = c.answers || [];
                    return (
                      <div key={c.id} className="report-user-item">
                        <div
                          className="report-user-header"
                          onClick={() => toggleUser(c.id)}
                        >
                          <input
                            type="checkbox"
                            className="report-checkbox"
                            checked={selectedReportIds.has(c.id)}
                            onChange={(e) => toggleReportSelection(c.id, e)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Select report for ${c.playerName || c.userEmail}`}
                          />
                          <div className="report-user-summary">
                            <span className="report-user-email">{c.userEmail}</span>
                            {(c.playerName || '').trim() && (
                              <span className="report-user-name">{c.playerName.trim()}</span>
                            )}
                            <span className="report-user-meta">
                              {c.score ?? 0}/{c.totalQuestions ?? 0}
                              {c.completedAt?.toDate?.() && ` · ${c.completedAt.toDate().toLocaleString()}`}
                            </span>
                          </div>
                          <div className="report-user-actions" onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className="btn-download-report"
                              onClick={(e) => downloadAttemptReport(e, c)}
                              title="Download this quiz report (PDF)"
                            >
                              <FileDown size={14} />
                              Download Report
                            </button>
                            {isDemoId(c.id) ? (
                              <button
                                type="button"
                                className="btn-download-report"
                                onClick={() => toastManager.info('View Report uses live data — expand this row to preview answers')}
                              >
                                <ExternalLink size={14} />
                                View Report
                              </button>
                            ) : (
                              <a
                                href={reportViewUrl(c.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn-download-report"
                              >
                                <ExternalLink size={14} />
                                View Report
                              </a>
                            )}
                          </div>
                        </div>
                        {isUserExpanded && answers.length > 0 && (
                          <div className="report-user-details">
                            <table className="report-answers-table">
                              <thead>
                                <tr>
                                  <th>Question</th>
                                  <th>Your Answer</th>
                                  <th>Correct Answer</th>
                                  <th>Result</th>
                                </tr>
                              </thead>
                              <tbody>
                                {answers.map((a, idx) => (
                                  <tr key={idx} className={a.isCorrect ? 'correct' : 'incorrect'}>
                                    <td>{a.questionText}</td>
                                    <td>{a.selectedAnswerText || '(not answered)'}</td>
                                    <td>{a.correctAnswerText || '—'}</td>
                                    <td>{a.isCorrect ? 'Correct' : 'Incorrect'}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {isUserExpanded && answers.length === 0 && (
                          <div className="report-user-details">
                            <p className="report-no-answers">No per-question data for this completion.</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {filteredSessions.length === 0 && searchQuery && (
        <div className="reports-empty">
          <p>No results for &quot;{searchQuery}&quot;</p>
        </div>
      )}

      {deleteCompletionConfirm && (
        <div className="confirm-overlay" onClick={() => setDeleteCompletionConfirm(null)}>
          <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
            {deleteCompletionConfirm.bulk ? (
              <>
                <h3>Delete selected reports?</h3>
                <p>
                  Delete {deleteCompletionConfirm.ids.length} report(s)? This cannot be undone.
                </p>
                <div className="confirm-actions">
                  <button type="button" className="confirm-btn cancel-btn" onClick={() => setDeleteCompletionConfirm(null)}>Cancel</button>
                  <button type="button" className="confirm-btn confirm-primary" onClick={handleBulkDelete}>Delete</button>
                </div>
              </>
            ) : (
              <>
                <h3>Delete report?</h3>
                <p>
                  Delete report for {deleteCompletionConfirm.playerName || deleteCompletionConfirm.userEmail}?
                  This cannot be undone.
                </p>
                <div className="confirm-actions">
                  <button type="button" className="confirm-btn cancel-btn" onClick={() => setDeleteCompletionConfirm(null)}>Cancel</button>
                  <button type="button" className="confirm-btn confirm-primary" onClick={() => handleDeleteCompletion(deleteCompletionConfirm.id)}>Delete</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Reports;
